import React, { createContext, useContext, useState } from 'react';
import { Button, Divider, Space, Steps } from 'antd';
import { BlockOutlined, PlusSquareOutlined } from '@ant-design/icons';
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";

function uuidv4() {
    return ([1e7] + -1e3 + -4e3 + -8e3 + -1e11).replace(/[018]/g, c =>
        (c ^ crypto.getRandomValues(new Uint8Array(1))[0] & 15 >> c / 4).toString(16)
    );
}

export default (formPlugin, namespace) =>
{
    const POSITION_VERTICAL = [
        t`Haut`,
        t`Bas`
    ]

    const POSITION_HORIZONTAL = [
        t`Gauche`,
        t`Centre`,
        t`Droite`
    ]

    const StepContext = createContext({ steps: [], current: 0 })

    const stepPlugin = {
        Renderer: (props) => {
            const { data } = props
            const stepContext = useContext(StepContext)

            if (stepContext.steps.some(i => i.nodeId === props.nodeId)) {
                stepContext.steps = stepContext.steps.map(i => {
                    if (i.nodeId === props.nodeId) {
                        i.data = data
                    }
                    return i
                })
            } else {
                stepContext.steps.push({ nodeId: props.nodeId, data: data })
            }

            const liveRender = (props.isPreviewMode || formPlugin.live)

            var display = "block"
            var index = stepContext.steps.findIndex(i => i.nodeId === props.nodeId)
            if (liveRender && index !== stepContext.current) {
                display = "none"
            }

            if (liveRender && index > stepContext.current) {
                return null
            }

            return (
                <div id={"form-step"} style={{
                    display: display,
                    border: liveRender ? "none" : "1px dashed orange",
                    borderRadius: liveRender ? 0 : 10,
                    padding: liveRender ? 0 : "2%"
                }}>
                    {!liveRender && <Divider>({data.title})</Divider>}
                    {props.children}
                </div>
            )
        },
        namespace: namespace,
        id: 'form-step' + namespace,
        title: t`Étape`,
        description: t`Élément de formulaire utilisable.`,
        icon: <PlusSquareOutlined />,
        iconColor: "#BA4A00",
        version: 1,
        cellPlugins: (plugins, data) => formPlugin.getPluginsMem(namespace).filter(i => !i.id.includes("form-step-container")),
        allowClickInside: true,
        isInlinable: false,
        allowInlineNeighbours: false,
        ignoreLayoutParams: true,
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    title: {
                        type: 'string',
                        uniforms: {
                            label: t`Titre`,
                        }
                    },
                    description: {
                        type: 'string',
                        uniforms: {
                            label: t`Description`,
                        }
                    }
                }
            },
        },
    }

    var localData = {}

    return {
        Renderer: (props) => {
            const { data } = props
            localData = data

            const stepContext = useContext(StepContext)
            const [current, setCurrent] = useState(stepContext.current);
            const [childKey, setChildKey] = useState(0);

            const changeCurrent = (value) => {
                if (current === value) {
                    return false
                }
                setCurrent(value)
                stepContext.current = value
                setChildKey(uuidv4())
            }

            var onChange = changeCurrent
            if ((props.isPreviewMode || props.readOnly) && !data.clickable) {
                onChange = null
            }

            if (stepContext.steps.length === 0 && formPlugin.live) {
                setTimeout(() => setChildKey(uuidv4()), 50)
            }

            const renderButtons = () => {
                var justifyContent = "flex-start"
                const buttonHorizontal = data.button_horizontal_position || POSITION_HORIZONTAL[2]
                switch (buttonHorizontal) {
                    case POSITION_HORIZONTAL[0]:
                        justifyContent = "flex-start"
                        break;
                    case POSITION_HORIZONTAL[1]:
                        justifyContent = "center"
                        break;
                    case POSITION_HORIZONTAL[2]:
                        justifyContent = "flex-end"
                        break;
                }

                return (
                    <div style={{
                        display: "flex",
                        justifyContent: justifyContent,
                        alignItems: "center",
                        margin: "10px 20px"
                    }}>
                        <Space>
                            {current !== 0 && (
                                <Button
                                    href={"#step-container-plugin_" + props.nodeId}
                                    onClick={() => { changeCurrent(current - 1) }}
                                    size={data.reduced ? "default" : "large"}
                                >
                                    <Trans>Précédent</Trans>
                                </Button>
                            )}

                            {current !== stepContext.steps.length - 1 && (
                                <Button
                                    href={"#step-container-plugin_" + props.nodeId}
                                    onClick={() => formPlugin.validateForm(() => { changeCurrent(current + 1) })}
                                    size={data.reduced ? "default" : "large"}
                                    type="primary"
                                >
                                    <Trans>Suivant</Trans>
                                </Button>
                            )}

                            {current === stepContext.steps.length - 1 && !data?.hideSubmit && (
                                <Button htmlType="submit" size={data.reduced ? "default" : "large"} type="primary">
                                    <Trans>Valider</Trans>
                                </Button>
                            )}
                        </Space>
                    </div>
                )
            }

            var buttonVertical = data.button_vertical_position || POSITION_VERTICAL[1]

            return (
                <div id={"step-container-plugin_" + props.nodeId}>
                    <Steps
                        progressDot={data.reduced}
                        direction="horizontal"
                        current={current}
                        onChange={onChange}
                        size={"small"}
                    >
                        {stepContext.steps.map(i => {
                            var stepProps = { ...i.data }
                            if (data.hideTitle) {
                                stepProps = {}
                            }
                            return <Steps.Step {...stepProps} />
                        })}
                    </Steps>

                    <div key={childKey} style={{ margin: "20px 0px" }}>
                        {buttonVertical === POSITION_VERTICAL[0] && renderButtons()}
                        {props.children}
                        {buttonVertical === POSITION_VERTICAL[1] && renderButtons()}
                    </div>
                </div>
            )
        },
        namespace: namespace,
        id: 'form-step-container' + namespace,
        title: t`Étapes`,
        description: t`Élément de formulaire utilisable.`,
        icon: <BlockOutlined />,
        iconColor: "#BA4A00",
        hideInMenu: formPlugin.formData.type !== "COMPLEX",
        version: 1,
        createInitialData: () => {
            return { id: uuidv4(), current: 0 }
        },
        cellPlugins: () => {
            var p = { ...stepPlugin }
            p.createInitialData = () => {
                return { id: uuidv4(), stepId: localData.id }
            }
            return [p]
        },
        cellSpacing: { x: 0, y: 0 },
        ignoreLayoutParams: true,
        controls: {
            type: 'autoform',
            schema: {
                properties: {
                    reduced: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Mini`,
                        }
                    },
                    hideTitle: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Masquer les titres et descriptions`,
                        }
                    },
                    clickable: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Onglets cliquables`,
                        }
                    },
                    button_vertical_position: {
                        type: "string",
                        uniforms: {
                            label: t`Positionnement vertical des boutons`,
                            allowedValues: POSITION_VERTICAL,
                        }
                    },
                    button_horizontal_position: {
                        type: "string",
                        uniforms: {
                            label: t`Positionnement horizontal des boutons`,
                            allowedValues: POSITION_HORIZONTAL,
                        }
                    },
                    hideSubmit: {
                        type: 'boolean',
                        uniforms: {
                            label: t`Masquer le bouton Valider`,
                        }
                    },
                }
            },
        },
    }
}
